import type { ApolloError } from '@apollo/client/core';

import {
  firstValidationMessage,
  isTechnicalMessage,
  NETWORK_ERROR_MESSAGE,
  toUserMessage,
} from './error';

type GraphQLErrorLike = { message?: string; extensions?: Record<string, unknown> };

/**
 * Extract the most useful, user-safe error message from an Apollo/GraphQL error.
 *
 * Priority:
 *  1. First Lighthouse `extensions.validation` message (the human rule message)
 *  2. First GraphQL error's extensions.debugMessage / extensions.reason, when it
 *     is written for humans (the real reason when Laravel masks a 500 as
 *     "Internal server error")
 *  3. Network failure → plain "check your connection" wording
 *  4. The GraphQL / network / Apollo message, passed through toUserMessage so
 *     technical text (masked 500s, "Variable $input…", SQLSTATE…) becomes plain wording
 *  5. Provided fallback
 *
 * When the server withheld a database error it returns a short
 * `extensions.reference`; that is appended so support can find the report.
 */
export function extractGraphQLError(error: unknown, fallback = 'An unexpected error occurred'): string {
  if (!error || typeof error !== 'object') return fallback;

  const validation = firstValidationMessage(error);
  if (validation) return validation;

  const apolloError = error as ApolloError;
  const firstGql = apolloError.graphQLErrors?.[0] as GraphQLErrorLike | undefined;
  const friendlyFallback = fallback === 'An unexpected error occurred' ? undefined : fallback;

  const ext = firstGql?.extensions as Record<string, unknown> | undefined;
  const withReference = (message: string) =>
    typeof ext?.reference === 'string' && ext.reference ? `${message} (Reference: ${ext.reference})` : message;

  for (const candidate of [ext?.debugMessage, ext?.reason]) {
    if (typeof candidate === 'string' && candidate && !isTechnicalMessage(candidate)) {
      return withReference(toUserMessage(candidate, friendlyFallback));
    }
  }

  if (firstGql?.message) return withReference(toUserMessage(firstGql.message, friendlyFallback));

  const netErr = apolloError.networkError as Record<string, unknown> | null;
  const networkResultMessage = netErr?.result
    ? (netErr.result as { errors?: Array<{ message?: string }> })?.errors?.[0]?.message
    : undefined;
  if (networkResultMessage) return toUserMessage(networkResultMessage, friendlyFallback);
  if (apolloError.networkError) {
    console.error(error);
    return NETWORK_ERROR_MESSAGE;
  }

  if (apolloError.message) return toUserMessage(apolloError.message, friendlyFallback);

  return fallback;
}
